import React from "react";
import { 
  EcommerceProductsProvider, 
  useEcommerceProducts, 
  AddToCartButton 
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious, 
  PaginationEllipsis 
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";


function ProductGridConsumer() {
  const { 
    products, 
    loading, 
    error, 
    refetch,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage
  } = useEcommerceProducts();

  
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div 
            key={i} 
            data-index={i} 
            className="flex flex-col space-y-4 rounded-xl border border-border bg-card p-4 shadow-sm"
          >
            <div className="aspect-[4/5] w-full animate-pulse rounded-lg bg-muted" />
            <div className="space-y-2">
              <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
              <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
            </div>
            <div className="mt-auto h-10 w-full animate-pulse rounded bg-muted" />
          </div>
        ))}
      </div>
    );
  }

  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">Unable to load products at this time.</p>
        <button 
          onClick={refetch} 
          className="mt-4 text-sm font-medium text-primary hover:underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  
  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-lg font-medium text-foreground">No products found.</p>
        <p className="text-muted-foreground">Check back later for new arrivals.</p>
      </div>
    );
  }

  
  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    
    if (startPage > 1) {
      items.push(
        <PaginationItem key="1">
          <PaginationLink 
            href="#" 
            onClick={(e) => { e.preventDefault(); setPage(1); }}
          >
            1
          </PaginationLink>
        </PaginationItem>
      );
      if (startPage > 2) {
        items.push(
          <PaginationItem key="ellipsis-start">
            <PaginationEllipsis />
          </PaginationItem>
        );
      }
    }

    
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink
            href="#"
            isActive={currentPage === i}
            onClick={(e) => { e.preventDefault(); setPage(i); }}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <PaginationItem key="ellipsis-end">
            <PaginationEllipsis />
          </PaginationItem>
        );
      }
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink 
            href="#" 
            onClick={(e) => { e.preventDefault(); setPage(totalPages); }}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return items;
  };

  return (
    <div className="space-y-12">
      {}
      <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:gap-x-8">
        {products.map((product, i) => (
          <div 
            key={product.id} 
            data-index={i}
            className="group relative flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-all hover:shadow-md"
          >
            {}
            <div className="aspect-[4/5] w-full overflow-hidden bg-muted">
              {product.featured_image_url ? (
                <Link to={product.link} className="block h-full w-full">
                  <img
                    src={product.featured_image_url}
                    alt={product.title}
                    className="h-full w-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </Link>
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                  <span className="text-sm">No Image</span>
                </div>
              )}
            </div>

            {}
            <div className="flex flex-1 flex-col p-4">
              <div className="mb-2">
                <h3 className="text-lg font-semibold text-foreground">
                  <Link to={product.link}>
                    <span aria-hidden="true" className="absolute inset-0" />
                    {product.title}
                  </Link>
                </h3>
                {product.categories && product.categories.length > 0 && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {product.categories[0].name}
                  </p>
                )}
              </div>
              
              <div className="mt-auto space-y-4">
                <div className="flex items-baseline justify-between">
                  <p className="text-xl font-bold text-primary">
                    {product.discounted_price ? (
                      <>
                        <span className="mr-2 text-sm text-muted-foreground line-through">
                          ${product.price}
                        </span>
                        ${product.discounted_price}
                      </>
                    ) : (
                      `$${product.price}`
                    )}
                  </p>
                </div>

                {}
                <div className="relative z-10">
                  <AddToCartButton
                    productId={product.id}
                    variant="default"
                    className="w-full"
                    buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                    text="Add to Cart"
                    showQuantity={false}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {}
      {totalPages > 1 && (
        <div className="mt-12 border-t border-border pt-8">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  href="#"
                  onClick={(e) => { e.preventDefault(); if(hasPrev) prevPage(); }}
                  className={cn(!hasPrev && "pointer-events-none opacity-50")}
                />
              </PaginationItem>
              
              {renderPaginationItems()}

              <PaginationItem>
                <PaginationNext 
                  href="#"
                  onClick={(e) => { e.preventDefault(); if(hasNext) nextPage(); }}
                  className={cn(!hasNext && "pointer-events-none opacity-50")}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}

export default function ProductGridAndPagination() {
  
  const current_wp_query_params = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : {};
    
  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section id="product-grid-and-pagination" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-10 md:mb-14">
          <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            Our Collection
          </h2>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Explore our premium selection of performance footwear designed for athletes who demand the best.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={wp_query} className="w-full">
          <ProductGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}